import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  User,
  Building2,
  Package,
  ShoppingCart,
  Users,
  BarChart3,
  Settings,
  Store,
  FileText,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AccessSummaryProps {
  showDetails?: boolean;
  onToggleDetails?: () => void;
}

const moduleCategories = [
  {
    name: 'Operaciones Diarias',
    modules: ['sales_pos', 'invoices', 'sales_cash_management', 'quotes', 'advance_orders'],
    icon: ShoppingCart,
    color: 'text-status-info'
  },
  {
    name: 'Inventario',
    modules: ['inventory_products', 'inventory_stock', 'inventory_categories', 'inventory_operations', 'inventory_kardex'],
    icon: Package,
    color: 'text-status-success'
  },
  {
    name: 'Administración',
    modules: ['administration', 'roles_permissions', 'system_modules'],
    icon: Users,
    color: 'text-primary'
  },
  {
    name: 'Reportes y Análisis',
    modules: ['reports', 'sales_targets'],
    icon: BarChart3,
    color: 'text-warning'
  }
];

export function RoleBasedAccessSummary({ showDetails = false, onToggleDetails }: AccessSummaryProps) {
  const { profile, selectedCompany } = useAuth();
  const { allowedModules, canViewModule } = useModulePermissions();
  const navigate = useNavigate();

  const getTotalAccess = () => {
    const totalModules = moduleCategories.reduce((acc, category) => acc + category.modules.length, 0);
    const accessibleModules = moduleCategories.reduce((acc, category) => {
      return acc + category.modules.filter(module => canViewModule(module)).length;
    }, 0);
    
    return { total: totalModules, accessible: accessibleModules };
  };

  const { total, accessible } = getTotalAccess();
  const accessPercentage = Math.round((accessible / total) * 100);

  const getAccessLevel = () => {
    if (accessPercentage >= 80) return { level: 'Completo', color: 'text-status-success', bg: 'bg-status-success-bg' };
    if (accessPercentage >= 60) return { level: 'Alto', color: 'text-status-info', bg: 'bg-status-info-bg' };
    if (accessPercentage >= 40) return { level: 'Medio', color: 'text-warning', bg: 'bg-status-warning-bg' };
    return { level: 'Limitado', color: 'text-status-error', bg: 'bg-status-error-bg' };
  };

  const accessInfo = getAccessLevel();

  return (
    <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-lg font-semibold">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
            <Shield className="h-4 w-4 text-primary" />
          </div>
          Acceso del Sistema
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground font-medium">
          Módulos disponibles para tu rol
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Summary Card */}
        <div className="bg-background/60 backdrop-blur-sm rounded-2xl p-4 border border-border/20">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">{profile?.full_name || 'Usuario'}</h4>
                <p className="text-xs text-muted-foreground">{selectedCompany?.name}</p>
              </div>
            </div>
            <Badge className={`${accessInfo.bg} ${accessInfo.color} border-none`}>
              {accessInfo.level}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Acceso a módulos:</span>
            <span className="font-semibold">{accessible}/{total} ({accessPercentage}%)</span>
          </div>
          
          <div className="w-full bg-muted/30 rounded-full h-2 mt-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500" 
              style={{ width: `${accessPercentage}%` }}
            />
          </div>
        </div>

        {/* Module Categories */}
        <div className="space-y-3">
          {moduleCategories.map((category, index) => {
            const categoryAccess = category.modules.filter(module => canViewModule(module));
            const hasAccess = categoryAccess.length > 0;
            const CategoryIcon = category.icon;
            
            return (
              <div 
                key={index} 
                className={`p-3 rounded-xl border transition-all duration-200 ${
                  hasAccess 
                    ? 'bg-background/60 border-border/20 hover:bg-background/80' 
                    : 'bg-muted/30 border-muted/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CategoryIcon className={`h-4 w-4 ${hasAccess ? category.color : 'text-muted-foreground'}`} />
                    <span className={`text-sm font-medium ${hasAccess ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {category.name}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-muted-foreground">
                      {categoryAccess.length}/{category.modules.length}
                    </span>
                    {hasAccess ? (
                      <CheckCircle className="h-4 w-4 text-status-success" />
                    ) : (
                      <XCircle className="h-4 w-4 text-status-error" />
                    )}
                  </div>
                </div>

                {showDetails && hasAccess && (
                  <div className="mt-2 space-y-1">
                    {category.modules.map(module => {
                      const hasModuleAccess = canViewModule(module);
                      if (!hasModuleAccess) return null;
                      
                      const moduleData = allowedModules.find(m => m.module_code === module);
                      return (
                        <div key={module} className="flex items-center justify-between pl-7 py-1">
                          <span className="text-xs text-muted-foreground">
                            {moduleData?.module_name || module}
                          </span>
                          <CheckCircle className="h-3 w-3 text-status-success" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          {onToggleDetails && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onToggleDetails}
              className="flex-1 text-xs apple-button"
            >
              {showDetails ? 'Ocultar' : 'Ver'} Detalles
            </Button>
          )}
          
          {canViewModule('administration') && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/admin/roles')}
              className="flex-1 text-xs apple-button"
            >
              <Settings className="h-3 w-3 mr-1" />
              Gestionar
            </Button>
          )}
        </div>

        {/* Help Text */}
        {accessible < total && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-amber-800">
                  Acceso limitado
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Contacta al administrador si necesitas acceso a más módulos.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}